import { useEffect, useState, Fragment, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface PromptRow {
  readonly promptId: string;
  readonly promptCode: string;
  readonly promptName: string;
  readonly promptCategory: string;
  readonly securityLevel: string;
  readonly currentVersionNo: number;
  readonly status: string;
}

interface VersionRow {
  readonly promptVersionId: string;
  readonly versionNo: number;
  readonly status: string;
  readonly testStatus: string;
  readonly changeNote?: string | null;
}

const CATEGORIES = ['SYSTEM', 'USER', 'CONTENT_GENERATION', 'SUMMARIZATION', 'EXTRACTION', 'QUERY', 'RECOMMENDATION', 'ANALYSIS'];
const SECURITY_LEVELS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const STATUSES = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'DEPRECATED', 'ARCHIVED'];
const PAGE = 25;

/**
 * M38 — Prompt Management Console (§13). The governed prompt library (T506) and
 * its immutable version history (T507, BR-06). Prompt engineers author a prompt
 * (PROMPT_MANAGE) which seeds version 1 as a DRAFT; versions are tested, then —
 * kept distinct by SoD (§27/BR-06) — approved and published by a separate
 * approver (PROMPT_APPROVE). Filterable by category, security level and status.
 * Tenant isolation is enforced server-side (LAW 4). A 403 surfaces a clear
 * permission message.
 */
export function PromptManagementConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PromptRow[]>([]);
  const [total, setTotal] = useState(0);
  const [promptCategory, setPromptCategory] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[1]);
  const [securityLevel, setSecurityLevel] = useState('INTERNAL');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<PromptRow | null>(null);

  // §13 version lifecycle (BR-06) — manage a single prompt's version history.
  const [manageId, setManageId] = useState<string | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const [newVersionText, setNewVersionText] = useState('');
  const [newVersionNote, setNewVersionNote] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (promptCategory) params.set('promptCategory', promptCategory);
    if (status) params.set('status', status);
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: PromptRow[]; total: number }>(`/v1/ai/prompts?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, promptCategory, status, page, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!name.trim()) { setActionError(t('m38.prompt.nameRequired')); return; }
    if (!templateText.trim()) { setActionError(t('m38.prompt.templateRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: PromptRow }>(`/v1/ai/prompts`, {
        method: 'POST',
        body: {
          promptName: name.trim(),
          templateText: templateText.trim(),
          promptCategory: newCategory,
          securityLevel,
        },
        token,
      });
      setCreated(r.data);
      setName('');
      setTemplateText('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m38.prompt.authorNotAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const permMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m38.prompt.notAuthorised', { perm })
      : (err as Error).message;

  const loadVersions = async (promptId: string): Promise<void> => {
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const r = await apiRequest<{ data: VersionRow[] }>(`/v1/ai/prompts/${promptId}/versions`, { token });
      setVersions(r.data);
    } catch (err) {
      setVersionsError(permMessage(err, 'PROMPT_VIEW'));
    } finally {
      setVersionsLoading(false);
    }
  };

  const openManage = async (promptId: string): Promise<void> => {
    setNotice(null);
    setVersionsError(null);
    if (manageId === promptId) { setManageId(null); return; }
    setManageId(promptId);
    setEditId(null);
    setVersions([]);
    await loadVersions(promptId);
  };

  const addVersion = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!manageId) return;
    setNotice(null);
    setVersionsError(null);
    if (!newVersionText.trim()) { setVersionsError(t('m38.prompt.versionTemplateRequired')); return; }
    setBusyVersionId('new');
    try {
      await apiRequest(`/v1/ai/prompts/${manageId}/versions`, {
        method: 'POST',
        body: { templateText: newVersionText.trim(), changeNote: newVersionNote.trim() || undefined },
        token,
      });
      setNewVersionText('');
      setNewVersionNote('');
      setNotice(t('m38.prompt.versionCreated'));
      await loadVersions(manageId);
      setReload((n) => n + 1);
    } catch (err) {
      setVersionsError(permMessage(err, 'PROMPT_MANAGE'));
    } finally {
      setBusyVersionId(null);
    }
  };

  const runVersionAction = async (
    versionId: string,
    path: string,
    body: Record<string, unknown> | undefined,
    perm: string,
    okMessage: string,
  ): Promise<void> => {
    if (!manageId) return;
    setNotice(null);
    setVersionsError(null);
    setBusyVersionId(versionId);
    try {
      await apiRequest(`/v1/ai/prompts/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(okMessage);
      await loadVersions(manageId);
      setReload((n) => n + 1);
    } catch (err) {
      setVersionsError(permMessage(err, perm));
    } finally {
      setBusyVersionId(null);
    }
  };

  const testVersion = (versionId: string, passed: boolean): Promise<void> =>
    runVersionAction(versionId, `versions/${versionId}/test`, { passed }, 'PROMPT_MANAGE', passed ? t('m38.prompt.versionTested') : t('m38.prompt.versionFailed'));

  const approveVersion = (versionId: string): Promise<void> =>
    runVersionAction(versionId, `versions/${versionId}/approve`, {}, 'PROMPT_APPROVE', t('m38.prompt.versionApproved'));

  const publishVersion = (versionId: string): Promise<void> =>
    runVersionAction(versionId, `versions/${versionId}/publish`, undefined, 'PROMPT_APPROVE', t('m38.prompt.versionPublished'));

  const startEdit = (row: PromptRow): void => {
    setNotice(null);
    setVersionsError(null);
    setEditId(row.promptId);
    setEditName(row.promptName);
  };

  const saveEdit = async (promptId: string): Promise<void> => {
    setNotice(null);
    setVersionsError(null);
    if (!editName.trim()) { setVersionsError(t('m38.prompt.nameRequired')); return; }
    setBusyVersionId('edit');
    try {
      await apiRequest(`/v1/ai/prompts/${promptId}`, {
        method: 'PUT',
        body: { promptName: editName.trim() },
        token,
      });
      setEditId(null);
      setNotice(t('m38.prompt.updated'));
      setReload((n) => n + 1);
    } catch (err) {
      setVersionsError(permMessage(err, 'PROMPT_MANAGE'));
    } finally {
      setBusyVersionId(null);
    }
  };

  const deletePrompt = async (promptId: string): Promise<void> => {
    setNotice(null);
    setVersionsError(null);
    setBusyVersionId('delete');
    try {
      await apiRequest(`/v1/ai/prompts/${promptId}`, { method: 'DELETE', token });
      setManageId(null);
      setNotice(t('m38.prompt.deleted'));
      setReload((n) => n + 1);
    } catch (err) {
      setVersionsError(permMessage(err, 'PROMPT_MANAGE'));
    } finally {
      setBusyVersionId(null);
    }
  };

  return (
    <main aria-label="prompt-management-console">
      <h1>{t('m38.prompt.title')}</h1>

      <section aria-label="create-prompt">
        <h2>{t('m38.prompt.authorHeading')}</h2>
        <form aria-label="prompt-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m38.common.name')}<input aria-label="field-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m38.prompt.templateText')}
            <textarea aria-label="field-template" value={templateText} onChange={(e) => setTemplateText(e.target.value)} rows={4} placeholder="e.g. Summarize {{topic}} for {{audience}}" />
          </label>
          <label>{t('m38.prompt.category')}
            <select aria-label="field-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m38.prompt.securityLevel')}
            <select aria-label="field-security" value={securityLevel} onChange={(e) => setSecurityLevel(e.target.value)}>
              {SECURITY_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-prompt" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.prompt.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="prompt-error">{actionError}</p>}
        {created && <p aria-label="prompt-created">{t('m38.prompt.created', { code: created.promptCode })}</p>}
      </section>

      <section aria-label="prompt-filters">
        <label>{t('m38.prompt.category')}
          <select aria-label="filter-category" value={promptCategory} onChange={(e) => { setPromptCategory(e.target.value); setPage(0); }}>
            <option value="">{t('m38.prompt.allCategories')}</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>{t('m38.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="prompt-total">{t('m38.prompt.total', { count: total })}</p>
          {notice && <p aria-label="version-notice">{notice}</p>}
          {versionsError && <p role="alert" aria-label="version-error">{versionsError}</p>}
          <table aria-label="prompt-table">
            <thead>
              <tr>
                <th scope="col">{t('m38.common.code')}</th><th scope="col">{t('m38.common.name')}</th><th scope="col">{t('m38.prompt.category')}</th>
                <th scope="col">{t('m38.prompt.colSecurity')}</th><th scope="col">{t('m38.common.version')}</th><th scope="col">{t('m38.common.status')}</th>
                <th scope="col">{t('m38.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>{t('m38.prompt.noneMatch')}</td></tr>
              ) : rows.map((p) => (
                <Fragment key={p.promptId}>
                  <tr data-prompt={p.promptCode}>
                    <td>{p.promptCode}</td>
                    <td>{p.promptName}</td>
                    <td>{p.promptCategory}</td>
                    <td>{p.securityLevel}</td>
                    <td aria-label={`version-${p.promptCode}`}>v{p.currentVersionNo}</td>
                    <td>{p.status}</td>
                    <td>
                      <Button variant="secondary" type="button" aria-label={`manage-${p.promptCode}`} onClick={() => void openManage(p.promptId)}>
                        {manageId === p.promptId ? t('m38.prompt.close') : t('m38.prompt.manage')}
                      </Button>
                      <Button variant="secondary" type="button" aria-label={`edit-${p.promptCode}`} onClick={() => startEdit(p)}>{t('m38.prompt.rename')}</Button>
                      <Button variant="danger" type="button" aria-label={`delete-${p.promptCode}`} disabled={busyVersionId === 'delete'} onClick={() => void deletePrompt(p.promptId)}>{t('common.delete')}</Button>
                    </td>
                  </tr>
                  {editId === p.promptId && (
                    <tr key={`${p.promptId}-edit`}>
                      <td colSpan={7}>
                        <label>{t('m38.prompt.newName')}
                          <input aria-label={`edit-name-${p.promptCode}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                        </label>
                        <Button variant="primary" type="button" aria-label={`save-edit-${p.promptCode}`} disabled={busyVersionId === 'edit'} onClick={() => void saveEdit(p.promptId)}>{t('common.save')}</Button>
                        <Button variant="danger" type="button" aria-label={`cancel-edit-${p.promptCode}`} onClick={() => setEditId(null)}>{t('common.cancel')}</Button>
                      </td>
                    </tr>
                  )}
                  {manageId === p.promptId && (
                    <tr key={`${p.promptId}-manage`}>
                      <td colSpan={7}>
                        <section aria-label={`versions-${p.promptCode}`}>
                          <h3>{t('m38.prompt.versionHistory')}</h3>
                          {versionsLoading ? <p>{t('m38.prompt.loadingVersions')}</p> : (
                            <table aria-label={`version-table-${p.promptCode}`}>
                              <thead>
                                <tr><th scope="col">{t('m38.common.version')}</th><th scope="col">{t('m38.common.status')}</th><th scope="col">{t('m38.prompt.colTest')}</th><th scope="col">{t('m38.common.actions')}</th></tr>
                              </thead>
                              <tbody>
                                {versions.length === 0 ? (
                                  <tr><td colSpan={4}>{t('m38.prompt.noVersions')}</td></tr>
                                ) : versions.map((v) => (
                                  <tr key={v.promptVersionId} data-version={v.versionNo}>
                                    <td>v{v.versionNo}</td>
                                    <td aria-label={`vstatus-${v.versionNo}`}>{v.status}</td>
                                    <td>{v.testStatus}</td>
                                    <td>
                                      {v.status === 'DRAFT' && (
                                        <>
                                          <Button variant="secondary" type="button" aria-label={`test-pass-${v.versionNo}`} disabled={busyVersionId === v.promptVersionId} onClick={() => void testVersion(v.promptVersionId, true)}>{t('m38.prompt.testPass')}</Button>
                                          <Button variant="secondary" type="button" aria-label={`test-fail-${v.versionNo}`} disabled={busyVersionId === v.promptVersionId} onClick={() => void testVersion(v.promptVersionId, false)}>{t('m38.prompt.testFail')}</Button>
                                        </>
                                      )}
                                      {v.status === 'TESTED' && (
                                        <Button variant="secondary" type="button" aria-label={`approve-${v.versionNo}`} disabled={busyVersionId === v.promptVersionId} onClick={() => void approveVersion(v.promptVersionId)}>{t('m38.prompt.approve')}</Button>
                                      )}
                                      {v.status === 'APPROVED' && (
                                        <Button variant="primary" type="button" aria-label={`publish-${v.versionNo}`} disabled={busyVersionId === v.promptVersionId} onClick={() => void publishVersion(v.promptVersionId)}>{t('m38.prompt.publish')}</Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <form aria-label={`version-form-${p.promptCode}`} onSubmit={(e) => void addVersion(e)}>
                            <label>{t('m38.prompt.newVersionTemplate')}
                              <textarea aria-label="field-version-template" value={newVersionText} onChange={(e) => setNewVersionText(e.target.value)} rows={3} />
                            </label>
                            <label>{t('m38.prompt.changeNote')}
                              <input aria-label="field-version-note" value={newVersionNote} onChange={(e) => setNewVersionNote(e.target.value)} />
                            </label>
                            <Button variant="primary" type="submit" aria-label="add-version" disabled={busyVersionId === 'new'}>{t('m38.prompt.addVersion')}</Button>
                          </form>
                        </section>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          <nav aria-label="prompt-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m38.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m38.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m38.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
